import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { loadSpreadImages } from 'editor/src/store/design/operation/setSpreadBackgroundsOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';
import {
  removeDesignSpreadsWithoutPages,
  SpreadsWithoutPagesReport,
} from 'editor/src/store/variants/helpers/removeSpreadsWithoutPages';
import getSpreadGroupsForGivenVariant from 'editor/src/store/variants/selector/getSpreadGroupsForGivenVariant';
import { addDesignTemplatesAction } from 'editor/src/store/variants/slice';

import { captureMessage } from 'editor/src/logger';

import applyTemplatesIfNeededOperation, { DesignTemplate } from './applyTemplatesIfNeededOperation';

const handleNewDesignTemplatesOperation =
  (templates: DesignTemplate[]) => async (dispatch: ThunkDispatch, getState: () => RootState) => {
    // A host can build a variant's template from its saved design, carrying over print areas saved without pages;
    // applied as they are, every step that reads a spread's first page fails. Grounds are matched to spreads by
    // name, so removing spreads leaves them aligned. Spread groups address spreads by index, and the stored
    // template gets its product's groups later (addDesignTemplatesReducer), so those templates are left alone.
    const { variants } = getState();
    const spreadsWithoutPages = templates
      .filter(
        (template) =>
          template.designData &&
          !template.designData.spread_groups &&
          !getSpreadGroupsForGivenVariant(variants, template.designData.product_uid)?.length,
      )
      .map((template) => removeDesignSpreadsWithoutPages(template.designData))
      .filter((report): report is SpreadsWithoutPagesReport => !!report);
    if (spreadsWithoutPages.length) {
      captureMessage('Removed design template spreads without pages', { extra: { templates: spreadsWithoutPages } });
    }

    await Promise.all(
      templates.map(async (template) => {
        if (template.spreadForegrounds) {
          template.spreadForegrounds = await loadSpreadImages(template.spreadForegrounds);
        }

        if (template.spreadBackgrounds) {
          template.spreadBackgrounds = await loadSpreadImages(template.spreadBackgrounds);
        }
      }),
    );

    batch(() => {
      dispatch(addDesignTemplatesAction(templates));
      dispatch(applyTemplatesIfNeededOperation(templates));
    });
  };

export default handleNewDesignTemplatesOperation;
